import type { ReactNode } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import cn from "@/v2-ui/utils/utils.cn"
import { FEATURE_CODE_PATTERN } from "@/v2-backoffice-client/feature/feature.constants"
import type { FeatureCreateIdentityValue } from "@/v2-backoffice-client/feature/create/feature.create.types"

export type FeatureCreateIdentityProps = {
  idPrefix: string
  codePlaceholder: string
  /** Names the thing in the copy, e.g. "basemap". */
  subject: string
  identity: FeatureCreateIdentityValue
  onIdentityChange(identity: FeatureCreateIdentityValue): void
  /** Kind-specific fields, rendered as further columns of the same row. */
  children?: ReactNode
}

/**
 * Who a new catalog entry is: feature code (the permanent identifier —
 * everything else can change later) and its en-GB name/description.
 *
 * Layers get their own richer version because a probed service prefills it
 * and the AI can re-propose it; basemaps and nav layers are hand-authored,
 * so this is the whole identity step for them.
 */
function FeatureCreateIdentity(props: FeatureCreateIdentityProps) {
  const { idPrefix, codePlaceholder, subject } = props
  const { identity, onIdentityChange, children } = props

  const isCodeValid =
    identity.featureCode === "" ||
    FEATURE_CODE_PATTERN.test(identity.featureCode)

  return (
    <div className="flex shrink-0 flex-wrap items-start gap-x-6 gap-y-3">
      <div className="flex min-w-64 flex-1 flex-col gap-1">
        <Label htmlFor={`${idPrefix}-feature-code`}>Feature code</Label>
        <Input
          id={`${idPrefix}-feature-code`}
          className={cn(
            "font-mono text-xs",
            !isCodeValid && "border-destructive",
          )}
          placeholder={codePlaceholder}
          value={identity.featureCode}
          onChange={(event) =>
            onIdentityChange({ ...identity, featureCode: event.target.value })
          }
        />
        <p className="text-[11px] text-muted-foreground">
          {isCodeValid
            ? "Permanent identifier — everything else can change later."
            : "Letters, digits and underscores only, starting with a letter."}
        </p>
      </div>

      <div className="flex min-w-56 flex-1 flex-col gap-1">
        <Label htmlFor={`${idPrefix}-name`}>Name (en-GB)</Label>
        <Input
          id={`${idPrefix}-name`}
          value={identity.name}
          onChange={(event) =>
            onIdentityChange({ ...identity, name: event.target.value })
          }
        />
        <p className="text-[11px] text-muted-foreground">
          Customer-facing; other languages via the {subject}&apos;s Details tab
          after creation.
        </p>
      </div>

      <div className="flex min-w-64 flex-[2] flex-col gap-1">
        <Label htmlFor={`${idPrefix}-description`}>Description (en-GB)</Label>
        <Textarea
          id={`${idPrefix}-description`}
          rows={2}
          value={identity.description}
          onChange={(event) =>
            onIdentityChange({ ...identity, description: event.target.value })
          }
        />
      </div>

      {children}
    </div>
  )
}

export default FeatureCreateIdentity
